import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS } from '../../assets/constants';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec3Green,
    colorAec4Orange,
    colorAec6Gray,
    colorAec8RedDark,
    colorBlack,
} from 'src/app/shared/all.constants';
import {
    ENEDIS_PRODUCERS_KPIS,
    PRODUCTION_FACILITIES_DISTRIBUTION_CHART,
    PRODUCTION_FACILITIES_EVOLUTION_CHART,
    PRODUCTION_FACILITIES_POWER_EVOLUTION_CHART,
} from '../../assets/keys';

const KEYS = [
    ENEDIS_PRODUCERS_KPIS,
    PRODUCTION_FACILITIES_DISTRIBUTION_CHART,
    PRODUCTION_FACILITIES_EVOLUTION_CHART,
    PRODUCTION_FACILITIES_POWER_EVOLUTION_CHART,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    producersNumberTotal?: number;
    producersNumberTotalEvolution?: number;

    powerTotal?: number;
    powerTotalEvolution?: number;
}

interface FacilitiesDistributionChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    hydraulic?: number;
    wind?: number;
    others?: number;
}

interface FacilitiesEvolutionChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    photovoltaic?: number;
    total?: number;
}

interface FacilitiesPowerEvolutionChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    hydraulic?: number;
    wind?: number;
    other?: number;
    photovoltaic?: number;
    total?: number;
}

@UntilDestroy()
@Component({
    selector: 'app-enedis-producers',
    templateUrl: './enedis-producers.component.html',
    styleUrls: ['./enedis-producers.component.scss'],
})
export class EnedisProducersComponent extends GenericComponent {
    public texts = CC_CONSTANTS.energyProducers.texts;
    public facilitiesDistributionChartSeries: ApexAxisChartSeries = [];
    public facilitiesEvolutionChartSeries: ApexAxisChartSeries = [];
    public facilitiesPowerEvolutionChartSeries: ApexAxisChartSeries = [];
    public facilitiesDistributionDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public facilitiesPowerEvolutionDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        style: this.setDataLabelsStyleColors(this.texts.facilitiesPowerEvolutionChart.totalTitle),
    };
    public facilitiesEvolutionDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: this.texts.facilitiesEvolutionChart.series[0].precision,
            1: this.texts.facilitiesEvolutionChart.series[1].precision,
        }),
    };
    public facilitiesEvolutionCustomTooltip: ApexTooltip['custom'];

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_PRODUCERS_KPIS),
    ) as Signal<KpiData[]>;

    public facilitiesDistributionChartData: Signal<FacilitiesDistributionChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(PRODUCTION_FACILITIES_DISTRIBUTION_CHART),
        ) as Signal<FacilitiesDistributionChartData[]>;
    public facilitiesEvolutionChartData: Signal<FacilitiesEvolutionChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(PRODUCTION_FACILITIES_EVOLUTION_CHART),
        ) as Signal<FacilitiesEvolutionChartData[]>;
    public facilitiesPowerEvolutionChartData: Signal<FacilitiesPowerEvolutionChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(PRODUCTION_FACILITIES_POWER_EVOLUTION_CHART),
        ) as Signal<FacilitiesPowerEvolutionChartData[]>;

    public facilitiesDistributionYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.facilitiesDistributionChart.yaxisTitleLeft,
            },
        },
    ];
    public facilitiesEvolutionYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.facilitiesEvolutionChart.series[0].name,
            min: 0,
            title: {
                text: this.texts.facilitiesEvolutionChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.facilitiesEvolutionChart.series[1].name,
            min: 0,
            opposite: true,
        },
    ];
    public facilitiesPowerEvolutionYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.facilitiesPowerEvolutionChart.yaxisTitleLeft,
            },
        },
    ];
    public facilitiesDistributionChartError: boolean = true;
    public facilitiesEvolutionChartError: boolean = true;
    public facilitiesPowerEvolutionChartError: boolean = true;

    public facilitiesEvolutionXaxisCategories: ApexXAxis['categories'];

    public facilitiesPowerLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec1BlueLight,
                colorAec4Orange,
                colorAec3Green,
                colorAec6Gray,
                colorBlack,
            ],
        },
    };
    public facilitiesPowerCustomTooltip: ApexTooltip['custom'];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const facilitiesDistributionDataList = this.facilitiesDistributionChartData();
            const facilitiesEvolutionDataList = this.facilitiesEvolutionChartData();
            const facilitiesPowerEvolutionDataList = this.facilitiesPowerEvolutionChartData();

            this.facilitiesDistributionChartError = this.checkForError(
                facilitiesDistributionDataList,
                ['xYear', 'hydraulic', 'wind', 'others'],
            );
            this.facilitiesEvolutionChartError = this.checkForError(facilitiesEvolutionDataList, [
                'xYear',
                'photovoltaic',
                'total',
            ]);

            if (!this.facilitiesDistributionChartError) {
                this.facilitiesDistributionChartSeries = [
                    {
                        name: this.texts.facilitiesDistributionChart.hydraulicTitle,
                        type: 'column',
                        data: facilitiesDistributionDataList.map((data) => ({
                            x: data.xYear,
                            y: data.hydraulic,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.facilitiesDistributionChart.windTitle,
                        type: 'column',
                        data: facilitiesDistributionDataList.map((data) => ({
                            x: data.xYear,
                            y: data.wind,
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.facilitiesDistributionChart.othersTitle,
                        type: 'column',
                        data: facilitiesDistributionDataList.map((data) => ({
                            x: data.xYear,
                            y: data.others,
                        })),
                        color: colorAec6Gray,
                    },
                ];
                this.facilitiesDistributionDataLabels.enabledOnSeries = [0, 1, 2];
            }

            if (!this.facilitiesEvolutionChartError) {
                this.facilitiesEvolutionXaxisCategories = facilitiesEvolutionDataList.map(
                    (data) => data.xYear,
                );

                this.facilitiesEvolutionChartSeries = [
                    {
                        name: this.texts.facilitiesEvolutionChart.series[0].name,
                        type: 'column',
                        data: facilitiesEvolutionDataList.map((data) => data.total!),
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.facilitiesEvolutionChart.series[1].name,
                        type: 'line',
                        data: facilitiesEvolutionDataList.map((data) => data.photovoltaic!),
                        color: colorAec3Green,
                    },
                ];
                this.facilitiesEvolutionDataLabels.enabledOnSeries = [0, 1];
            }

            this.facilitiesPowerEvolutionChartError = this.checkForError(
                facilitiesPowerEvolutionDataList,
                ['xYear', 'hydraulic', 'wind', 'other', 'photovoltaic'],
            );

            if (!this.facilitiesPowerEvolutionChartError) {
                const totales = this.getTotales(facilitiesPowerEvolutionDataList, [
                    'hydraulic',
                    'wind',
                    'other',
                    'photovoltaic',
                ]);
                this.facilitiesPowerEvolutionChartSeries = [
                    {
                        name: this.texts.facilitiesPowerEvolutionChart.hydraulicTitle,
                        type: 'column',
                        data: facilitiesPowerEvolutionDataList.map((data) => ({
                            x: data.xYear,
                            y: data.hydraulic,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.facilitiesPowerEvolutionChart.windTitle,
                        type: 'column',
                        data: facilitiesPowerEvolutionDataList.map((data) => ({
                            x: data.xYear,
                            y: data.wind,
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.facilitiesPowerEvolutionChart.photovoltaicTitle,
                        type: 'column',
                        data: facilitiesPowerEvolutionDataList.map((data) => ({
                            x: data.xYear,
                            y: data.photovoltaic,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.facilitiesPowerEvolutionChart.othersTitle,
                        type: 'column',
                        data: facilitiesPowerEvolutionDataList.map((data) => ({
                            x: data.xYear,
                            y: data.other,
                        })),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.facilitiesPowerEvolutionChart.totalTitle,
                        type: 'line',
                        data: facilitiesPowerEvolutionDataList.map((data, index) => ({
                            x: data.xYear,
                            y: totales[index],
                        })),
                        color: 'transparent',
                    },
                ];
                this.facilitiesPowerEvolutionDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });
        this.facilitiesEvolutionCustomTooltip = this.setFacilitiesEvolutionCustomTooltip();

        this.facilitiesPowerCustomTooltip = this.setFacilitiesPowerCustomTooltip();
    }

    private setFacilitiesEvolutionCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.facilitiesEvolutionChart.series[0].precision;
            const totalSerie = w.globals.initialSeries[0];
            const {
                name: totalName,
                color: totalColor,
                valueStringified: totalStringified,
            } = this.setCustomTooltipData(precision, totalSerie, dataPointIndex);

            const ratePrecision = this.texts.facilitiesEvolutionChart.series[1].precision;
            const rateSerie = w.globals.initialSeries[1];
            const {
                name: rateName,
                color: rateColor,
                valueStringified: rateStringified,
            } = this.setCustomTooltipData(ratePrecision, rateSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: totalColor,
                    name: totalName,
                    value: totalStringified,
                },
                {
                    color: rateColor,
                    name: rateName,
                    value: rateStringified + ' %',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public setFacilitiesPowerCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const labels = w.globals.labels;
            const title = labels[dataPointIndex];

            const precision = 2;
            const hydraulicSerie = w.globals.initialSeries[0];
            const {
                name: hydraulicName,
                color: hydraulicColor,
                valueStringified: hydraulicStringified,
            } = this.setCustomTooltipData(precision, hydraulicSerie, dataPointIndex);

            const windSerie = w.globals.initialSeries[1];
            const {
                name: windName,
                color: windColor,
                valueStringified: windStringified,
            } = this.setCustomTooltipData(precision, windSerie, dataPointIndex);

            const photovoltaicSerie = w.globals.initialSeries[2];
            const {
                name: photovoltaicName,
                color: photovoltaicColor,
                valueStringified: photovoltaicStringified,
            } = this.setCustomTooltipData(precision, photovoltaicSerie, dataPointIndex);

            const otherSerie = w.globals.initialSeries[3];
            const {
                name: otherName,
                color: otherColor,
                valueStringified: otherStringified,
            } = this.setCustomTooltipData(precision, otherSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[4];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: hydraulicColor,
                    name: hydraulicName,
                    value: hydraulicStringified + ' MVA',
                },
                {
                    color: windColor,
                    name: windName,
                    value: windStringified + ' MVA',
                },
                {
                    color: photovoltaicColor,
                    name: photovoltaicName,
                    value: photovoltaicStringified + ' MVA',
                },
                {
                    color: otherColor,
                    name: otherName,
                    value: otherStringified + ' MVA',
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified + ' MVA',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }
}
